import { useMemo } from 'react';

import { gql } from '@apollo/client';
import Cookies from 'js-cookie';

import { useMutation, useQuery } from 'platform/apollo';
import { useAuth } from 'platform/auth-fe';
import {
  GetFeatureFlagRequest,
  GetFeatureFlagRequestVariables,
} from 'platform/graphql/__generated__/GetFeatureFlagRequest';
import {
  GetSeveralFeatureFlagsRequest,
  GetSeveralFeatureFlagsRequestVariables,
} from 'platform/graphql/__generated__/GetSeveralFeatureFlagsRequest';
import {
  GetSeveralVariantsRequest,
  GetSeveralVariantsRequestVariables,
} from 'platform/graphql/__generated__/GetSeveralVariantsRequest';
import {
  GetVariantRequest,
  GetVariantRequestVariables,
} from 'platform/graphql/__generated__/GetVariantRequest';

import {
  AMPLITUDE_CONTEXT_EXPERIMENTS_LIST,
  AMPLITUDE_EXPERIMENT_LIST_TYPES,
  WEBVIEW_EXPERIMENTS_ALLOWLIST,
} from 'shared/declarations/AmplitudeExperiments';
import {
  AMPLITUDE_CONTEXT_FLAGS_LIST,
  WEBVIEW_FEATURE_FLAGS_ALLOWLIST,
} from 'shared/declarations/AmplitudeFeatureFlags';
import { useBrowserCompatibilityData } from 'shared/helpers/globals';

import { usePartnershipReferrals } from 'growth/usePartnershipReferrals';

import { useExperimentationContext } from 'sellDirect/ExperimentationContext';
import { useSellerLead } from 'sellDirect/graphql/SellerLeadContext';

import { SetImpression, SetImpressionVariables } from './__generated__/SetImpression';

/**
 * Used to get the value of a feature flag or experiment from a variant list
 * to do: remove type checks when we deprecate optimizely
 */
export const getAmplitudeResult = (
  key: string,
  variantList?: Array<string | Record<string, string>>,
): string | undefined => {
  let result;
  if (variantList) {
    result = variantList.find((variant) => {
      return typeof variant === 'object' && Object.keys(variant).includes(key);
    });
  }
  return result === undefined || typeof result === 'string' ? undefined : result[key];
};

/**
 * For more information about flags and experimentation with Amplitude see:
 * https://opendoor.atlassian.net/wiki/spaces/CONSUMER/pages/2248114307/Experimentation+with+Amplitude
 */
interface AmplitudeResult {
  loading: boolean;
  variant: string;
}
interface AmplitudeResultList {
  loading: boolean;
  variants: Record<string, string>[];
}

interface Variant {
  key: string;
  value: { value: string } | null;
}

interface DefaultValues {
  [key: string]: { defaultValue: string };
}

/**
 * Helper function that handles the result of querying the variant service
 * Adds default values if the variant value is not found
 */
export const combineVariantsDataWithDefaults = (
  isWebView: boolean,
  variantsData: Array<Variant>,
  defaultValues: DefaultValues,
): Array<{ [key: string]: string }> => {
  const variantsMap = new Map<string, string | undefined>(
    variantsData.map((variant) => [variant.key, variant?.value?.value ?? undefined]),
  );

  const combinedVariants: Array<{ [key: string]: string }> = [];
  Object.entries(defaultValues).forEach(([key, defaultValue]) => {
    const variantValue = variantsMap.get(key);
    const variantInfo: { [key: string]: string } = {};

    if (
      !variantValue ||
      (isWebView && !WEBVIEW_FEATURE_FLAGS_AND_EXPERIMENTS_ALLOWLIST.includes(key))
    ) {
      if (defaultValue.defaultValue) {
        variantInfo[key] = defaultValue.defaultValue;
      }
    } else {
      variantInfo[key] = variantValue;
    }

    // Only add to the array if there is at least one valid key-value pair
    if (Object.keys(variantInfo).length > 0) {
      combinedVariants.push(variantInfo);
    }
  });

  return combinedVariants;
};
/**
 * Helper function that returns the default value if request is not made
 */
const defaultVariantValues = (defaultValues: Record<string, { defaultValue: string }>) => {
  const variantNames = Object.keys(defaultValues);
  return variantNames.map((variantName) => {
    const currentVariantInfo: Record<string, string> = {};
    currentVariantInfo[variantName] = defaultValues[variantName]?.defaultValue;
    return currentVariantInfo;
  });
};

/**
 * Helper function that checks if we're using a webview and if we are if the variant is on the whitelist
 */
export const isVariantEnabled = (
  isWebView: boolean,
  experimentOrFFName: string,
  variantValue: string,
  defaultValue: string,
  isLoading: boolean,
): AmplitudeResult => {
  if (isWebView && !WEBVIEW_FEATURE_FLAGS_AND_EXPERIMENTS_ALLOWLIST.includes(experimentOrFFName)) {
    return {
      loading: isLoading,
      variant: defaultValue,
    };
  }
  return {
    loading: isLoading,
    variant: variantValue,
  };
};

/**
 * Queries the Amplitude variant service to get the variant for the given experiment
 * This allows you to pass custom props into a single experiment
 */
export const useGetAmplitudeExperiment = (
  experiment: GetVariantRequestVariables['key'],
  defaultValue: string,
  opts?: {
    // isLoading: allows you to run query if input you want to provide is still loading
    isLoading?: boolean;
    // autoImpression: set to true if the user is being exposed to the experiment right away
    autoImpression?: GetVariantRequestVariables['autoImpression'];
    // props: allows you to pass custom props to the experiment
    props?: GetVariantRequestVariables['props'];
    skip?: boolean;
  },
): AmplitudeResult => {
  const { isWebView } = useBrowserCompatibilityData();
  const { autoImpression, props } = opts || {};
  const isLoading = opts?.isLoading || false;
  const { user } = useAuth();
  const anonymousId = Cookies.get('ajs_anonymous_id');
  const {
    data,
    loading: loadingQuery,
    error,
  } = useQuery<GetVariantRequest, GetVariantRequestVariables>(GET_AMPLITUDE_EXPERIMENT, {
    skip:
      isLoading ||
      // In case of custom criteria that we want to exclude upon.
      opts?.skip ||
      // Skip query altogether if the experiment is not on the allow list for mobile app
      (isWebView && !WEBVIEW_FEATURE_FLAGS_AND_EXPERIMENTS_ALLOWLIST.includes(experiment)),
    variables: {
      grain: {
        grain: {
          customer: {
            primaryId: { customerUuid: user?.customerUuid ?? null },
            temporaryId: { anonymousId: anonymousId ?? null },
          },
        },
      },
      key: experiment,
      props,
      autoImpression,
    },
    fetchPolicy: 'cache-first',
  });

  const amplitudeLoading = loadingQuery || isLoading;
  const enabledExperiment = useMemo(() => {
    const variantValue = data?.variant?.getVariant?.variant?.value;
    if (!variantValue) {
      return {
        loading: amplitudeLoading,
        variant: defaultValue,
      };
    }
    return isVariantEnabled(isWebView, experiment, variantValue, defaultValue, amplitudeLoading);
  }, [data, defaultValue, amplitudeLoading, experiment, isWebView]);
  if (error) {
    return {
      loading: amplitudeLoading,
      variant: defaultValue,
    };
  }
  return enabledExperiment;
};

/**
 * Queries the Amplitude variant service to get several variants for the given experiment names
 */
export const useGetSeveralAmplitudeExperiments = (
  experiments: Record<AMPLITUDE_CONTEXT_EXPERIMENTS_LIST, { defaultValue: string }>,
  opts?: { isLoading?: boolean },
): AmplitudeResultList => {
  const { isWebView } = useBrowserCompatibilityData();
  let { isLoading } = opts || {};
  isLoading = isLoading || false;
  const keys = Object.keys(experiments);
  let filteredKeys = keys;
  if (isWebView) {
    filteredKeys = keys.filter((key) =>
      WEBVIEW_FEATURE_FLAGS_AND_EXPERIMENTS_ALLOWLIST.includes(key),
    );
  }
  const { user } = useAuth();
  const anonymousId = Cookies.get('ajs_anonymous_id');
  const { data, loading: loadingQuery } = useQuery<
    GetSeveralVariantsRequest,
    GetSeveralVariantsRequestVariables
  >(GET_SEVERAL_AMPLITUDE_EXPERIMENTS, {
    skip: isLoading || filteredKeys.length === 0,
    variables: {
      grain: {
        grain: {
          customer: {
            primaryId: { customerUuid: user?.customerUuid ?? null },
            temporaryId: { anonymousId: anonymousId ?? null },
          },
        },
      },
      keys: filteredKeys,
      props: {
        fields: [
          {
            key: 'userAgent',
            value: { kind: { stringValue: navigator.userAgent } },
          },
        ],
      },
    },
    fetchPolicy: 'cache-first',
  });
  const amplitudeLoading = loadingQuery || isLoading;
  const enabledExperiments = useMemo(() => {
    if (data?.variant?.getVariants?.variants !== undefined) {
      return {
        loading: amplitudeLoading,
        variants: combineVariantsDataWithDefaults(
          isWebView,
          data?.variant?.getVariants?.variants,
          experiments,
        ),
      };
    }
    return {
      loading: amplitudeLoading,
      variants: defaultVariantValues(experiments),
    };
  }, [data, amplitudeLoading, experiments, isWebView]);
  return enabledExperiments;
};

/**
 * Lets you make a request to Journey to set an impression for an experiment variant you've already requested.
 * @param {AMPLITUDE_EXPERIMENT_LIST_TYPES} key The name of the experiment
 * @returns {Array} The first element is the function you call to make the request.
 * The second element is the Apollo object { data, loading, error }.
 */
export const useSetExperimentImpression = (
  key: AMPLITUDE_EXPERIMENT_LIST_TYPES,
  // optional way to pass in your variant value result
  // this is useful if your experiment does not live on the experimentationContext
  variantValueResult?: string,
) => {
  const { user } = useAuth();
  const anonymousId = Cookies.get('ajs_anonymous_id');
  const { enabledExperimentVariations } = useExperimentationContext();
  const variantValue = variantValueResult ?? getAmplitudeResult(key, enabledExperimentVariations);
  const [setImpression, { data, loading, error }] = useMutation<
    SetImpression,
    SetImpressionVariables
  >(SET_IMPRESSION, {
    variables: {
      grain: {
        grain: {
          customer: {
            primaryId: { customerUuid: user?.customerUuid ?? null },
            temporaryId: { anonymousId: anonymousId ?? null },
          },
        },
      },
      key,
      // @ts-expect-error -- fixme(@core-experience-platform) variable is possibly undefined/null
      variantValue,
    },
  });
  return [setImpression, { data, loading, error }] as const;
};

/**
 * Queries the Amplitude variant service to get the variant for the given featureFlag
 * This allows you to pass custom props into a single featureFlag
 */
export const useGetAmplitudeFeatureFlag = (
  featureFlag: string,
  defaultValue: string,
  opts?: {
    // isLoading: allows you to run query if input you want to provide is still loading
    isLoading?: boolean;
    // props: allows you to pass custom props to the feature flag
    props?: GetFeatureFlagRequestVariables['props'];
  },
): AmplitudeResult => {
  const { isWebView } = useBrowserCompatibilityData();
  const { props } = opts || {};
  const isLoading = opts?.isLoading || false;
  const { user } = useAuth();
  const anonymousId = Cookies.get('ajs_anonymous_id');
  const {
    data,
    error,
    loading: loadingQuery,
  } = useQuery<GetFeatureFlagRequest, GetFeatureFlagRequestVariables>(GET_AMPLITUDE_FEATURE_FLAG, {
    skip: isLoading,
    variables: {
      grain: {
        grain: {
          customer: {
            primaryId: { customerUuid: user?.customerUuid ?? null },
            temporaryId: { anonymousId: anonymousId ?? null },
          },
        },
      },
      keys: [featureFlag],
      props,
    },
    fetchPolicy: 'cache-first',
  });

  const amplitudeLoading = loadingQuery || isLoading;
  const enabledExperiment = useMemo(() => {
    if (!data?.variant?.getFeatureFlags?.variants[0].value?.value) {
      return {
        loading: amplitudeLoading,
        variant: defaultValue,
      };
    }
    const variantValue = data?.variant?.getFeatureFlags?.variants[0].value.value;
    return isVariantEnabled(isWebView, featureFlag, variantValue, defaultValue, amplitudeLoading);
  }, [
    data?.variant?.getFeatureFlags?.variants,
    featureFlag,
    defaultValue,
    amplitudeLoading,
    isWebView,
  ]);
  if (error) {
    return {
      loading: amplitudeLoading,
      variant: defaultValue,
    };
  }
  return enabledExperiment;
};

/**
 * Queries the Amplitude variant service to get several variants for the given feature flags
 */
export const useGetSeveralAmplitudeFeatureFlags = (
  featureFlags: Record<AMPLITUDE_CONTEXT_FLAGS_LIST, { defaultValue: string }>,
  opts?: { isLoading?: boolean },
): AmplitudeResultList => {
  let { isLoading } = opts || {};
  isLoading = isLoading || false;
  const keys = Object.keys(featureFlags);
  const { user } = useAuth();
  const anonymousId = Cookies.get('ajs_anonymous_id');
  const { lead, loading: sellerLeadLoading, sellerInputUUID } = useSellerLead();
  const {
    loading: partnershipsLoading,
    partnershipAttributes,
    hasHomebuilderReferral,
  } = usePartnershipReferrals(sellerInputUUID);

  const fields = [
    {
      value: { kind: { stringValue: navigator.userAgent } },
      key: 'userAgent',
    },
    {
      key: 'city',
      value: { kind: { stringValue: lead?.address.city } },
    },
    {
      key: 'market',
      value: { kind: { stringValue: lead?.region } },
    },
    {
      key: 'hasHomebuilderReferral',
      value: { kind: { stringValue: hasHomebuilderReferral.toString() } },
    },
    {
      key: 'sellerLeadUuid',
      value: { kind: { stringValue: lead?.id } },
    },
    {
      key: 'customerEmail',
      value: { kind: { stringValue: lead?.customerV2?.primaryEmail?.address?.value ?? '' } },
    },
  ];

  if (partnershipAttributes?.partnership_identifiers) {
    fields.push({
      key: 'partnership_identifiers',
      value: { kind: { stringValue: partnershipAttributes?.partnership_identifiers } },
    });
  }
  const { isWebView } = useBrowserCompatibilityData();
  const { data, loading: loadingQuery } = useQuery<
    GetSeveralFeatureFlagsRequest,
    GetSeveralFeatureFlagsRequestVariables
  >(GET_SEVERAL_AMPLITUDE_FEATURE_FLAGS, {
    skip: isLoading || sellerLeadLoading || partnershipsLoading || keys.length === 0,
    variables: {
      grain: {
        grain: {
          customer: {
            primaryId: { customerUuid: user?.customerUuid ?? null },
            temporaryId: { anonymousId: anonymousId ?? null },
          },
        },
      },
      keys,
      props: { fields },
    },
    fetchPolicy: 'cache-first',
  });
  const amplitudeLoading = loadingQuery || isLoading;
  const enabledExperiments = useMemo(() => {
    if (data?.variant?.getFeatureFlags?.variants !== undefined) {
      return {
        loading: amplitudeLoading,
        variants: combineVariantsDataWithDefaults(
          isWebView,
          data?.variant?.getFeatureFlags?.variants,
          featureFlags,
        ),
      };
    }
    return {
      loading: amplitudeLoading,
      variants: defaultVariantValues(featureFlags),
    };
  }, [data, amplitudeLoading, featureFlags, isWebView]);
  return enabledExperiments;
};

/**
 * NOTE: If you want your experiment/feature flag to run in the Opendoor mobile app, you
 * need to add it to the allow list below. Please test your experiment in the
 * mobile app before adding it below to make sure it behaves as intended.
 * Instructions here: https://opendoor.atlassian.net/wiki/spaces/CONSUMER/pages/2317353092/Experimentation+Feature+Flagging+With+Amplitude#How-to-test-that-your-FF/Experiment-is-showing-up-on-WebViews
 */
export const WEBVIEW_FEATURE_FLAGS_AND_EXPERIMENTS_ALLOWLIST: string[] = [
  'webview-test',
  // Do not remove "webview-test" from the allow list as this is used for testing purposes
  // Add your experiment/feature flag to the allow list in src/shared/declarations/AmplitudeExperiments.ts or src/shared/declarations/AmplitudeFeatureFlags.ts
  ...WEBVIEW_FEATURE_FLAGS_ALLOWLIST,
  ...WEBVIEW_EXPERIMENTS_ALLOWLIST,
];

export const GET_AMPLITUDE_EXPERIMENT = gql`
  query GetVariantRequest(
    $grain: OdProtosVariantData_GrainInput
    $key: String!
    $props: GoogleProtobuf_StructInput
    $autoImpression: Boolean
  ) {
    variant {
      getVariant(
        input: { grain: $grain, key: $key, props: $props, autoImpression: $autoImpression }
      ) {
        key
        variant {
          value
        }
      }
    }
  }
`;

export const GET_SEVERAL_AMPLITUDE_EXPERIMENTS = gql`
  query GetSeveralVariantsRequest(
    $grain: OdProtosVariantData_GrainInput
    $keys: [String!]
    $props: GoogleProtobuf_StructInput
  ) {
    variant {
      getVariants(input: { grain: $grain, keys: $keys, props: $props }) {
        variants {
          key
          value {
            value
          }
        }
      }
    }
  }
`;

export const SET_IMPRESSION = gql`
  mutation SetImpression(
    $grain: OdProtosVariantData_GrainInput
    $key: String!
    $variantValue: String!
  ) {
    variant {
      setImpression(input: { grain: $grain, key: $key, variantValue: $variantValue }) {
        _empty
      }
    }
  }
`;

export const GET_SEVERAL_AMPLITUDE_FEATURE_FLAGS = gql`
  query GetSeveralFeatureFlagsRequest(
    $grain: OdProtosVariantData_GrainInput
    $keys: [String!]
    $props: GoogleProtobuf_StructInput
  ) {
    variant {
      getFeatureFlags(input: { grain: $grain, keys: $keys, props: $props }) {
        variants {
          key
          value {
            value
          }
        }
      }
    }
  }
`;

export const GET_AMPLITUDE_FEATURE_FLAG = gql`
  query GetFeatureFlagRequest(
    $grain: OdProtosVariantData_GrainInput
    $keys: [String!]
    $props: GoogleProtobuf_StructInput
  ) {
    variant {
      getFeatureFlags(input: { grain: $grain, keys: $keys, props: $props }) {
        variants {
          key
          value {
            value
          }
        }
      }
    }
  }
`;
